import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { X, Plus, Users, Folder } from "lucide-react"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"
import { darfAufgabeAnlegen } from "@/lib/aufgabeVerknuepfung"
import { kontaktName, searchHeroContacts, searchHeroProjects } from "@/lib/heroSearch"
import { HeroVorgangName } from "./HeroVorgangName"
import { VORGANG_OHNE_NAME, vorgangBezeichnung } from "@/lib/vorgangName"

type Assignee = { user_id: number; name: string }

/** Ab dieser Laenge schlaegt der Dialog eine KI-Kurzfassung vor (#143).
 *  Gleicher Wert wie `services/aufgabe_kurzfassung.py::SCHWELLE`. */
export const KURZFASSUNG_AB = 400
// Firmenkontakte haben in HERO NUR `company_name` — ohne das Feld blieb die
// Zeile hier namenlos (#52).
type ContactHit = { id: number; first_name?: string; last_name?: string; company_name?: string; email?: string; phone?: string }
// v1-Schema (HeroProjectHit aus heroSearch); legacy-Felder beibehalten falls
// noch ein anderer Aufrufer das alte Format liefert.
type ProjectHit = {
  id: number
  name?: string
  code?: string | null
  contact_name?: string
  status?: string
  // legacy
  project_title?: string
  project_nr?: string
}

/**
 * Task Create-Modal. Nutzt bestehende Endpoints:
 * - GET /api/v1/tasks/assignees?tenant= — Monteure-Dropdown
 * - POST /admin/tasks/api/create — anlegen
 */
export type TaskWerte = {
  title: string
  comment: string
  due_date: string
  /** "id|name" — dasselbe Format wie beim Anlegen. */
  target_user: string
  contact_id: number | null
  contact_name: string
  project_match_id: number | null
  project_name: string
}

export function TaskCreateModal({
  open, onClose, defaultTenant, initial, onSave, titel,
}: {
  open: boolean
  onClose: () => void
  defaultTenant?: string
  /**
   * Bearbeiten statt Anlegen. Ist `onSave` gesetzt, legt der Dialog NICHTS an
   * — er gibt die Werte zurueck. Gebraucht fuer die Aufgaben-Aktionen im
   * Agent-Plan: dort entsteht die Aufgabe erst beim Ausfuehren, der Dialog
   * bearbeitet nur den Vorschlag.
   *
   * Bewusst derselbe Dialog und nicht ein zweiter, aehnlicher: Kontakt- und
   * Projekt-Picker, die Entweder-Oder-Regel von HERO und das Mitarbeiter-
   * Dropdown sind hier erprobt. Ein Nachbau haette sie alle nochmal gebraucht.
   */
  initial?: Partial<TaskWerte>
  onSave?: (werte: TaskWerte) => void
  titel?: string
}) {
  const qc = useQueryClient()
  const [title, setTitle] = useState("")
  const [comment, setComment] = useState("")
  const [dueDate, setDueDate] = useState("")
  const [tenant, setTenant] = useState(defaultTenant || "")
  const [assignee, setAssignee] = useState("")
  const [contact, setContact] = useState<{ id: number; name: string } | null>(null)
  const [project, setProject] = useState<{ id: number; name: string } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  // #143 (Paul, 30.09.2026): „aufgabenbeschreibung kommt mir sehr lange vor.
  // Das sollte zusammengefasst werden von der KI." Die Kurzfassung ersetzt den
  // Text SICHTBAR vor dem Speichern — mit Weg zurueck zum Original. Wer das
  // Original zurueckholt, bekommt keinen zweiten Vorschlag.
  const [original, setOriginal] = useState<string | null>(null)
  const [kuerzt, setKuerzt] = useState(false)
  const kurzAbgelehnt = useRef(false)
  const kommentarJetzt = useRef(comment)
  kommentarJetzt.current = comment

  const kuerzen = async (text: string) => {
    if (kurzAbgelehnt.current || kuerzt || text.trim().length < KURZFASSUNG_AB) return
    setKuerzt(true)
    try {
      const r = await fetch("/api/v1/tasks/kurzfassung", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, tenant: tenant || defaultTenant || null }),
      })
      const d = r.ok ? await r.json() : null
      // Nur ersetzen, wenn in der Zwischenzeit niemand weitergetippt hat.
      if (d?.kurz && kommentarJetzt.current === text) {
        setOriginal(text)
        setComment(d.kurz)
      }
    } catch {
      // Ohne Kurzfassung bleibt der Text, wie er ist.
    } finally {
      setKuerzt(false)
    }
  }

  useEffect(() => {
    if (!open) return
    setErr(null)
    setOriginal(null)
    kurzAbgelehnt.current = false
    if ((initial?.comment ?? "").trim().length >= KURZFASSUNG_AB) void kuerzen(initial?.comment ?? "")
    setTitle(initial?.title ?? "")
    setComment(initial?.comment ?? "")
    setDueDate(initial?.due_date ?? "")
    setAssignee(initial?.target_user ?? "")
    setContact(initial?.contact_id
      ? { id: initial.contact_id, name: initial.contact_name || `Kontakt ${initial.contact_id}` } : null)
    setProject(initial?.project_match_id
      ? { id: initial.project_match_id, name: initial.project_name || `Projekt ${initial.project_match_id}` } : null)
    setTenant(defaultTenant || "")
    // Nur beim Oeffnen — sonst wuerde jedes Tippen zurueckgesetzt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTenant])

  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
    enabled: open,
  })

  const assignees = useQuery<{ monteure: Assignee[]; hinweis?: string | null }>({
    queryKey: ["task-assignees", tenant],
    queryFn: async () => {
      const res = await fetch(`/api/v1/tasks/assignees?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: open && !!tenant,
  })

  // Backend-Provider → fortytools kennt keine HERO-Kontakt/Projekt-Verknüpfung
  // (die Picker suchen in HERO). Für fortytools ausblenden.
  const tenantInfo = useQuery<{ backend_provider?: string } | null>({
    queryKey: ["tenant-provider", tenant],
    queryFn: async () => {
      if (!tenant) return null
      const res = await fetch(`/api/v1/tenants/${encodeURIComponent(tenant)}`, { credentials: "include" })
      if (!res.ok) return null
      return res.json()
    },
    enabled: open && !!tenant,
  })
  const isForty = (tenantInfo.data?.backend_provider || "hero") === "fortytools"

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/v1/tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title, comment, due_date: dueDate, tenant_slug: tenant,
          target_user: assignee,  // "id|name" Format
          contact_id: contact?.id ? String(contact.id) : "",
          project_match_id: project?.id ? String(project.id) : "",
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      return json
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
      onClose()
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  if (!open) return null

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      data-kontext-popup={onSave ? "Plan-Aufgabe bearbeiten" : "Aufgabe anlegen"}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <form
        onSubmit={async (ev) => {
          ev.preventDefault()
          setErr(null)
          if (onSave) {
            onSave({
              title, comment, due_date: dueDate, target_user: assignee,
              contact_id: contact?.id ?? null, contact_name: contact?.name || "",
              project_match_id: project?.id ?? null, project_name: project?.name || "",
            })
            onClose()
            return
          }
          // #149 (+ #22): Hinweis ohne Mitarbeiter / ohne Projekt — VOR dem
          // Absenden, damit „Abbrechen" den Dialog offen laesst. Kontakt und
          // Projekt schliessen einander hier aus, keines zu waehlen geht.
          if (!(await darfAufgabeAnlegen({ mitarbeiter: assignee, contactId: contact?.id, projectId: project?.id }))) return
          create.mutate()
        }}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 480,
        }}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-[15px] font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> {titel || (onSave ? "Aufgabe bearbeiten" : "Neue Aufgabe")}</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          {err && <div className="pp-alert pp-alert-err">{err}</div>}

          <div>
            <label className="pp-form-label">Titel *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="z.B. Rückruf bei Hans Müller" />
          </div>

          <div>
            <label className="pp-form-label">Kommentar</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onBlur={(e) => { void kuerzen(e.target.value) }}
              rows={3} placeholder="Details / Notizen..."
            />
            {kuerzt && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>KI fasst die Beschreibung zusammen …</div>
            )}
            {original !== null && !kuerzt && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                KI-Kurzfassung ·{" "}
                <button
                  type="button"
                  className="pp-link"
                  style={{ background: "none", border: 0, padding: 0, color: "var(--accent-color)", cursor: "pointer" }}
                  onClick={() => { kurzAbgelehnt.current = true; setComment(original); setOriginal(null) }}
                >
                  Original wiederherstellen
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="pp-form-label">Fällig (Datum)</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>

          <div>
            <label className="pp-form-label">Tenant</label>
            <select value={tenant} onChange={(e) => setTenant(e.target.value)}>
              <option value="">– Tenant –</option>
              {tenantsDd.data?.items?.map((t) => (
                <option key={t.slug} value={t.slug}>{t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="pp-form-label">Mitarbeiter</label>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} disabled={!tenant}>
              <option value="">– Mitarbeiter –</option>
              {assignees.data?.monteure?.map((m) => (
                <option key={m.user_id} value={`${m.user_id}|${m.name}`}>{m.name}</option>
              ))}
            </select>
            {assignees.data && !assignees.data.monteure?.length && (
              <div className="text-xs" data-testid="keine-mitarbeiter" style={{ marginTop: 4, color: "var(--text-muted, #6b7280)" }}>
                {assignees.data.hinweis || "Keine Mitarbeiter hinterlegt."}
              </div>
            )}
            {assignees.isError && (
              <div className="text-xs" style={{ marginTop: 4, color: "#b91c1c" }}>Mitarbeiterliste konnte nicht geladen werden.</div>
            )}
          </div>

          {/* HERO erlaubt nur ENTWEDER Kontakt ODER Projekt fuer Tasks.
              Beide Picker bleiben sichtbar; der jeweils andere wird
              ausgegraut, sobald einer gesetzt ist (sonst wirkt das Projekt
              "verschwunden", wenn ein Kontakt gewaehlt wurde).
              Bei fortytools ausgeblendet (Picker suchen in HERO). */}
          {!isForty && (
            <>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: -4 }}>
                HERO erlaubt nur entweder Kontakt <strong>oder</strong> Projekt — nicht beides:
              </div>
              <HeroContactPicker tenant={tenant} value={contact} onChange={setContact} disabled={!!project} />
              <HeroProjectPicker tenant={tenant} contactId={null} value={project} onChange={setProject} disabled={!!contact} />
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="pp-btn pp-btn-sm">Abbrechen</button>
          <button
            type="submit"
            disabled={!title || create.isPending}
            className={cn("pp-btn pp-btn-primary pp-btn-sm", (!title || create.isPending) && "opacity-50 cursor-not-allowed")}
          >
            {onSave ? "Übernehmen" : create.isPending ? "Erstellt..." : "Erstellen"}
          </button>
        </div>
      </form>
    </div>
  )
}

/** HERO-Kontakt-Picker (Autocomplete). Genutzt in Task-Create/Edit. */
export function HeroContactPicker({ tenant, value, onChange, label = "Kontakt (HERO)", disabled = false }: {
  tenant: string
  value: { id: number; name: string } | null
  onChange: (v: { id: number; name: string } | null) => void
  label?: string
  disabled?: boolean
}) {
  const [q, setQ] = useState("")
  const [hits, setHits] = useState<ContactHit[]>([])
  const debRef = useRef<number | null>(null)
  const search = (text: string) => {
    setQ(text)
    if (debRef.current) window.clearTimeout(debRef.current)
    const s = text.trim()
    if (!tenant || s.length < 2) { setHits([]); return }
    debRef.current = window.setTimeout(async () => {
      try {
        const r = await searchHeroContacts(tenant, s)
        setHits(r.slice(0, 8))
      } catch {
        // Vorschlagsliste, kein Arbeitsschritt — leeren statt eine Liste zum
        // vorigen Suchwort stehen zu lassen (#52).
        setHits([])
      }
    }, 250)
  }
  const pick = (c: ContactHit) => {
    const name = kontaktName(c)
    onChange({ id: c.id, name })
    setQ(""); setHits([])
  }
  return (
    <div>
      <label className="pp-form-label flex items-center gap-1"><Users className="w-3 h-3" /> {label} <span className="text-[10px] text-muted-foreground font-normal">(optional)</span></label>
      {value ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 10px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
          <span style={{ flex: 1, fontSize: 12.5 }}>{value.name} <span style={{ color: "var(--text-muted)", fontSize: 12 }}>#{value.id}</span></span>
          <button type="button" onClick={() => onChange(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626" }}>✕</button>
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <input
            value={q}
            onChange={(e) => search(e.target.value)}
            placeholder={disabled ? "Projekt gewählt – zum Wechseln Projekt entfernen" : (tenant ? "Name / E-Mail / Telefon…" : "Zuerst Tenant wählen")}
            disabled={disabled || !tenant}
          />
          {hits.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, background: "var(--bg-card)", border: "1px solid var(--border-color)", borderRadius: 6, marginTop: 2, maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
              {hits.map((c) => {
                const name = kontaktName(c)
                return (
                  <button key={c.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(c) }}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", background: "none", border: "none", cursor: "pointer", fontSize: 12 }}
                    className="hover:bg-secondary">
                    <div style={{ fontWeight: 600 }}>{name}</div>
                    <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{[c.email, c.phone].filter(Boolean).join(" · ")}</div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** HERO-Projekt-Picker. Optional mit contactId-Filter. */
export function HeroProjectPicker({ tenant, contactId, value, onChange, label = "Projekt (HERO)", disabled = false }: {
  tenant: string
  contactId?: number | null
  value: { id: number; name: string } | null
  onChange: (v: { id: number; name: string } | null) => void
  label?: string
  disabled?: boolean
}) {
  const [q, setQ] = useState("")
  const [hits, setHits] = useState<ProjectHit[]>([])
  const debRef = useRef<number | null>(null)
  const search = (text: string) => {
    setQ(text)
    if (debRef.current) window.clearTimeout(debRef.current)
    const s = text.trim()
    if (!tenant || s.length < 2) { setHits([]); return }
    debRef.current = window.setTimeout(async () => {
      const r = await searchHeroProjects(tenant, s)
      // contactId-Filter: aktuell nicht genutzt — v1-Endpoint filtert nicht
      // serverseitig, das Backend-Pendant unterstuetzt es auch nicht. Falls
      // gebraucht, spaeter erweitern.
      void contactId
      setHits(r.slice(0, 8))
    }, 250)
  }
  const pick = (p: ProjectHit) => {
    // #98: leer statt „Projekt #…" — die Anzeige schlaegt dann in HERO nach.
    const name = vorgangBezeichnung(p.code || p.project_nr, p.name || p.project_title) || ""
    onChange({ id: p.id, name })
    setQ(""); setHits([])
  }
  return (
    <div>
      <label className="pp-form-label flex items-center gap-1"><Folder className="w-3 h-3" /> {label} <span className="text-[10px] text-muted-foreground font-normal">(optional)</span></label>
      {value ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 10px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
          <span style={{ flex: 1, fontSize: 12.5 }}>
            <HeroVorgangName tenantSlug={tenant} projectId={value.id} bekannt={value.name} />
          </span>
          <button type="button" onClick={() => onChange(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626" }}>✕</button>
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <input
            value={q}
            onChange={(e) => search(e.target.value)}
            placeholder={disabled ? "Kontakt gewählt – zum Wechseln Kontakt entfernen" : (tenant ? (contactId ? "Projekte des Kontakts suchen…" : "Projektname suchen…") : "Zuerst Tenant wählen")}
            disabled={disabled || !tenant}
          />
          {hits.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, background: "var(--bg-card)", border: "1px solid var(--border-color)", borderRadius: 6, marginTop: 2, maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
              {hits.map((p) => (
                <button key={p.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(p) }}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", background: "none", border: "none", cursor: "pointer", fontSize: 12 }}
                  className="hover:bg-secondary">
                  <div style={{ fontWeight: 600 }}>
                    {(p.code || p.project_nr) && <span style={{ background: "var(--accent-light)", color: "var(--accent-color)", padding: "0 5px", borderRadius: 3, fontSize: 12, marginRight: 6 }}>{p.code || p.project_nr}</span>}
                    {p.name || p.project_title || (p.code || p.project_nr ? "" : VORGANG_OHNE_NAME)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
